import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import './TutorialModal.css';
import { expandirSeColapsada } from '../utils/revelarAlvo';

interface TutorialModalProps {
  onClose: () => void;
  showIntro?: boolean;
}

/**
 * Cada passo tem DUAS cores, e precisa ter.
 *
 * O acento pinta o título do card, o anel do holofote e o fundo do botão
 * "Próximo". No claro isso funciona com a cor cheia; no escuro o card é
 * `#1c1c23` e a mesma cor cheia vira texto ilegível (`#374151` sobre ele dá
 * ~1,5:1). É a armadilha 9 do handoff: um token serve a um PAPEL, e "acento
 * sobre superfície" muda de direção com o fundo.
 *
 * Pela mesma regra, a tinta SOBRE o acento inverte junto — branco no claro,
 * quase-preto no escuro (`--t-on-p`).
 */
interface Step {
  id: string;
  icon: string;
  color: string;
  colorDark: string;
  title: string;
  desc: string;
  tips: string[];
}

const STEPS: Step[] = [
  {
    id: 'qe-campo',
    icon: '⚡',
    color: '#7c3aed',
    colorDark: '#b69bff',
    title: 'Lançar escrevendo',
    desc: 'A forma principal de registrar: escreva como você fala e o app interpreta.',
    tips: [
      '"terça gastei 50 em uber no crédito" vira um lançamento',
      'O que faltar vem do seu histórico, você só confirma',
      '"zara 3x de 129,90" já entra como compra parcelada',
      '"investi 5 mil em CDB" vai para Investimentos, não para gastos',
    ],
  },
  {
    id: 'sec-form',
    icon: '💸',
    color: '#7c3aed',
    colorDark: '#b69bff',
    title: 'Formulário completo',
    desc: 'Continua aqui, recolhido, para o que a interpretação não cobrir.',
    tips: [
      'Abra em "Preencher manualmente", abaixo do campo',
      'Ele nasce preenchido com o que já foi interpretado',
      'Marque Mensal para repetir, ou Parcelado para dividir',
      'Um recebimento pode sair de um investimento ou de uma reserva',
    ],
  },
  /*
   * Scan e Colar viraram um passo só (22/10): são a mesma coisa, o comprovante
   * lido pela IA, e o tour estava longo demais.
   */
  {
    id: 'tf-scan-btn-wrap',
    icon: '📷',
    color: '#7c3aed',
    colorDark: '#b69bff',
    title: 'Comprovante e fatura',
    desc: 'Mande a imagem e a IA preenche por você.',
    tips: [
      'Scan: escolha a foto ou o print do comprovante',
      'O ícone ao lado cola uma imagem que você copiou',
      'O print da fatura inteira vira vários lançamentos de uma vez',
      'Você confere antes de salvar',
    ],
  },
  /* O mês da tela (22/10): tudo abaixo dele mostra o mês escolhido aqui. */
  {
    id: 'mes-tela',
    icon: '📅',
    color: '#7c3aed',
    colorDark: '#b69bff',
    title: 'O mês da tela',
    desc: 'Tudo o que aparece abaixo é do mês escolhido aqui.',
    tips: [
      'As setas andam um mês; clicar no nome abre os doze meses',
      'Meses futuros mostram o que já está previsto: parcelas, contas fixas',
      '"Voltar para hoje" aparece quando você sai do mês atual',
    ],
  },
  {
    /*
     * A faixa `bs-faixa` foi removida em 27/08 e este passo aponta para o
     * CARD, que é onde o saldo mora agora.
     *
     * Passo apontando para elemento inexistente não dá erro: o tutorial
     * simplesmente ilumina o vazio, e ninguém descobre até alguém rodar o
     * tour inteiro. Há teste de fiação para isso.
     */
    id: 'summary-card-saldo',
    icon: '💰',
    color: '#0e7490',
    colorDark: '#67d3ee',
    title: 'Saldo',
    desc: 'O número que responde "quanto eu tenho", sem você precisar pedir.',
    tips: [
      'É o mesmo saldo que Projeções mostra, e que você edita lá',
      'Mudando o mês lá em cima, ele passa a ser o saldo daquele mês',
      'Obedece ao olhinho de ocultar valores',
      'Conta ligada a lembrete e ainda não paga não é descontada: o dinheiro ainda está lá',
    ],
  },
  {
    id: 'ac-ancora',
    icon: '💬',
    color: '#4f46e5',
    colorDark: '#a5a7ff',
    title: 'Perguntar',
    desc: 'O chat que responde sobre o seu próprio dinheiro, no canto da tela.',
    tips: [
      '"quanto posso gastar esse mês?", "quanto gastei em delivery?"',
      'Simule antes de comprar: "posso comprar um sofá de 3000?"',
      'Dá para aprofundar: "e em julho?", "e se fosse 2000?"',
      'Todo número da resposta é calculado pelo app, nunca escrito pela IA',
    ],
  },
  {
    id: 'sec-methods',
    icon: '💳',
    color: '#0e7490',
    colorDark: '#67d3ee',
    title: 'Métodos de Pagamento',
    desc: 'Seus cartões e contas, e o que cada um te dá.',
    tips: [
      '"nubank crédito, limite 5 mil, fecha dia 12" cadastra o cartão',
      'Informe bandeira e nível para ver as vantagens que você já tem',
      'Seus pontos: quanto valem em reais e qual cartão pontua mais',
      'Acompanhe a fatura e quanto do limite já foi usado',
    ],
  },
  {
    id: 'sec-investments',
    icon: '📈',
    color: '#047857',
    colorDark: '#5fd3a8',
    title: 'Investimentos',
    desc: 'Acompanhe sua carteira, e o que ela rende, em um só lugar.',
    tips: [
      '"investi 5 mil em CDB no Nubank que rende 6% ao ano" cria a aplicação',
      'Aportar tira o dinheiro do caixa e registra a transação',
      'Editar o valor é "rendeu" ou "corrigi", e isso não mexe no caixa',
      'O rendimento previsto entra na projeção, separado do caixa',
    ],
  },
  {
    id: 'sec-debts',
    icon: '🏦',
    color: '#dc2626',
    colorDark: '#ff9d9d',
    title: 'Dívidas',
    desc: 'Consignado, financiamento, empréstimo: o que já está contratado.',
    tips: [
      '"fiz um consignado de 53 mil em 36x de 1950" cadastra a dívida',
      'O mesmo campo responde: "de onde sai a parcela do consignado?"',
      'Diga se a parcela sai do salário, aí ela não vira lançamento',
      'A barra mostra quanto do total já foi pago',
    ],
  },
  {
    id: 'sec-goals',
    icon: '🎯',
    color: '#db2777',
    colorDark: '#f890bd',
    title: 'Metas',
    desc: 'Defina objetivos e acompanhe o progresso.',
    tips: [
      '"quero juntar 20 mil pra viagem até dezembro" cria a meta com prazo',
      '"não quero gastar mais de 500 com delivery pelos próximos 6 meses" vira limite',
      'Com prazo, a meta diz quanto separar por mês; sem prazo, diz quando você chega',
      'Aportar em uma meta registra a saída do caixa',
    ],
  },
  {
    id: 'sec-projections',
    icon: '📊',
    color: '#b45309',
    colorDark: '#f0b45e',
    title: 'Projeções',
    desc: 'Como o seu saldo caminha nos próximos meses.',
    tips: [
      'Em Caixa é o que dá para gastar; Patrimônio são reservas e investimentos',
      'Estimado projeta o gasto variável a partir do seu histórico',
      'Clique num mês para filtrar as outras seções por ele',
      'O campo "de onde vem esse número?" explica qualquer linha',
    ],
  },
  {
    id: 'sec-reminders',
    icon: '🔔',
    color: '#7c3aed',
    colorDark: '#b69bff',
    title: 'Lembretes',
    desc: 'Nunca esqueça uma conta ou vencimento.',
    tips: [
      '"todo dia 10 pagar aluguel 1200" cria o lembrete',
      'O aviso chega por e-mail no dia e também no WhatsApp, se estiver ligado',
      'Marque como pago para controlar as quitações',
      'Também dá para cadastrar recebimento: "recebo salário dia 30"',
    ],
  },
  /* Nossa conta (casa compartilhada): só o que é da casa, nunca o pessoal. */
  {
    id: 'sec-casa',
    icon: '🏠',
    color: '#0e7490',
    colorDark: '#67d3ee',
    title: 'Nossa conta',
    desc: 'Para quem divide a casa: para onde foi o dinheiro de todos.',
    tips: [
      'Convide quem mora com você pelo email',
      'Marque um lançamento como da casa e ele aparece aqui',
      'Mostra por categoria, não quem gastou o quê',
      'O seu gasto pessoal nunca aparece para a casa',
    ],
  },
  /* Só existe para quem comprou moeda; sem a seção, o tour pula este passo. */
  {
    id: 'sec-moedas',
    icon: '💱',
    color: '#047857',
    colorDark: '#5fd3a8',
    title: 'Outras moedas',
    desc: 'O dinheiro da viagem, em outra moeda.',
    tips: [
      'A compra da moeda sai do caixa uma vez só',
      'Cada gasto lá fora conta pelo preço que você pagou na moeda',
      'O modo viagem lança na moeda pelo WhatsApp',
    ],
  },
  {
    id: 'sec-chart',
    icon: '🥧',
    color: '#2563eb',
    colorDark: '#90b4ff',
    title: 'Categorias',
    desc: 'Visualize onde seu dinheiro está indo.',
    tips: [
      'Gráfico de pizza por categoria de gasto do período',
      'Clique numa fatia para filtrar as transações',
      'Pergunte ali embaixo: "para onde meu dinheiro foi?"',
      'Crie categorias personalizadas, com cor própria',
    ],
  },
  {
    id: 'sec-transactions',
    icon: '📋',
    color: '#374151',
    colorDark: '#c2c6d0',
    title: 'Transações',
    desc: 'Histórico completo, e dá para perguntar em vez de filtrar.',
    tips: [
      '"quais os parcelamentos que fiz em dezembro?" monta o filtro sozinho',
      'Os chips mostram o que foi entendido da sua pergunta',
      'Filtre por tipo e categoria, edite ou exclua qualquer lançamento',
      'Em parcelada, a lista mostra qual parcela cai no mês exibido',
    ],
  },
  {
    id: 'nav-avatar-btn',
    icon: '📱',
    color: '#047857',
    colorDark: '#5fd3a8',
    title: 'WhatsApp',
    desc: 'Lançar e consultar pela conversa, sem abrir o site.',
    tips: [
      'Abra o menu do seu avatar → WhatsApp para parear',
      'O pareamento é um código de 6 dígitos, mandado do SEU número',
      'Depois é só escrever "mercado 187,40 no débito" na conversa',
      'Errou? "corrigir: era 80" ajusta o último lançamento',
    ],
  },
  /*
   * O resto do menu do avatar num passo só (22/10). Claude e ChatGPT tinha
   * passo próprio, e dizia que o conector não cria nada — ele lança, quando
   * pedido. Três passos seguidos no mesmo botão cansavam.
   */
  {
    id: 'nav-avatar-btn',
    icon: '🧭',
    color: '#5b21b6',
    colorDark: '#c4b5fd',
    title: 'No seu menu',
    desc: 'O que mais o app faz, no menu do seu avatar.',
    tips: [
      'Claude e ChatGPT: converse sobre o seu dinheiro na IA que você já usa',
      'Open Finance, quando liberado: o banco manda os lançamentos sozinho',
      'Newsletter diária: seus números e as notícias, nos dias úteis',
      'Compartilhar relatório: o resumo do mês para quem você escolher',
      'Meu plano: ver, trocar ou cancelar',
    ],
  },
  {
    id: 'nav-eye-btn',
    icon: '👁️',
    color: '#4f46e5',
    colorDark: '#a5a7ff',
    title: 'Ocultar Valores',
    desc: 'Esconda os valores financeiros com um clique.',
    tips: [
      'Clique no ícone de olho na barra superior',
      'Vale para os cards, para a faixa de saldo e para o chat',
      'A preferência é salva automaticamente',
    ],
  },
  {
    id: 'nav-theme-picker',
    icon: '🎨',
    color: '#7e5aa8',
    colorDark: '#c7b5e4',
    title: 'Tema e modo escuro',
    desc: 'Personalize a aparência do aplicativo.',
    tips: [
      'Escolha a cor do app: roxo, azul, verde, rosa e outras',
      'O modo escuro fica no mesmo lugar, junto da cor',
      'Muda na hora e fica salvo neste aparelho',
    ],
  },
  {
    id: 'nav-layout-btn',
    icon: '⊞',
    color: '#2d1b69',
    colorDark: '#b3a3e8',
    title: 'Personalizar Layout',
    desc: 'Reorganize as seções do jeito que preferir.',
    tips: [
      'No desktop: arraste as seções livremente',
      'No mobile: reordene pela lista com setas ↑↓',
      'Expanda seções para ocupar metade ou toda a largura da tela',
      'O layout é salvo automaticamente',
    ],
  },
];
interface CardPosition {
  top: number;
  left: number;
  placement: 'right' | 'left' | 'bottom' | 'top';
}

/*
 * Item do menu do topo: fixo, sempre à vista, não rola.
 *
 * Era "altura menor que 60px", e o campo de "Lançar escrevendo" também é baixo
 * (22/10): o tour achava que era do menu, não rolava, e o card ficava espremido
 * no pé da tela, cortado. Quem é do menu é quem está DENTRO dele.
 */
const naNavbar = (el: Element): boolean => !!el.closest('.app-nav');

const CARD_WIDTH = 300;
const CARD_HEIGHT = 260;

/**
 * Claro ou escuro, reagindo à troca sem remontar o tour.
 *
 * `applyTheme` escreve `data-theme` no `:root`, então é lá que se observa —
 * mesmo motivo do `utils/tokenCss.ts`. Aqui não dá para usar `var()`: a cor do
 * passo é um valor do próprio passo, não um token do CSS.
 */
function useEscuro(): boolean {
  const ler = () => document.documentElement.dataset.theme === 'dark';
  const [escuro, setEscuro] = useState(ler);

  useEffect(() => {
    const obs = new MutationObserver(() => setEscuro(ler()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    setEscuro(ler());
    return () => obs.disconnect();
  }, []);

  return escuro;
}

const TutorialModal: React.FC<TutorialModalProps> = ({ onClose, showIntro = false }) => {
  const [atIntro, setAtIntro] = useState(showIntro);
  const [step, setStep] = useState(0);
  /**
   * Para onde ela estava indo. Só serve para pular passo cujo elemento não
   * está na tela: pular "para frente" quando ela veio do "Próximo" e "para
   * trás" quando veio do "←" é o que mantém o botão fazendo o que promete.
   */
  const [rumo, setRumo] = useState<1 | -1>(1);
  /*
   * Clicar fora minimiza, não fecha (22/10). Ela queria experimentar o que o
   * passo acabou de explicar e voltar ao tour depois; fechar obrigava a
   * recomeçar do passo 1. Minimizado, a tela fica livre e sobra uma pílula
   * "Continuar tour", com um × para quem quer mesmo sair.
   */
  const [minimizado, setMinimizado] = useState(false);
  /** Muda a cada "Continuar": faz o passo atual rolar e se posicionar de novo. */
  const [retomada, setRetomada] = useState(0);
  const [cardPos, setCardPos] = useState<CardPosition>({ top: 80, left: 20, placement: 'right' });
  const [spotRect, setSpotRect] = useState<DOMRect | null>(null);
  /*
   * No celular o card é uma folha presa embaixo. Quando o alvo mora na metade
   * de baixo da tela — a bolha do "Perguntar", no canto — a folha cobria
   * justamente o que explica (22/10). Aí ela sobe para o topo.
   */
  const sheetNoTopo = !!spotRect && spotRect.top + spotRect.height / 2 > window.innerHeight / 2
    && spotRect.height < window.innerHeight / 2;
  const [ready, setReady] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const escuro = useEscuro();
  /**
   * `onClose` chega como arrow inline do `App`, então muda de identidade a
   * cada render dele. Como dependência do efeito de posicionamento, isso
   * refaria o `scrollIntoView` do passo atual sem motivo — o card saltando
   * sozinho. O ref quebra o vínculo sem esconder a dependência de verdade.
   */
  const fecharRef = useRef(onClose);
  fecharRef.current = onClose;

  const current = STEPS[step];
  const acento = escuro ? current.colorDark : current.color;
  /** A tinta que vai SOBRE o acento inverte junto com ele (regra do `--t-on-p`). */
  const sobreAcento = escuro ? '#16121f' : '#ffffff';
  const isLast = step === STEPS.length - 1;

  const updatePosition = useCallback(() => {
    const el = document.getElementById(current.id);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;

    // Elementos pequenos recebem padding extra no spotlight
    const pad = rect.height < 60 ? 10 : 5;
    const isNavItem = naNavbar(el);

    // Clamp spotlight to visible viewport
    const visTop    = Math.max(rect.top    - pad, margin);
    const visBottom = Math.min(rect.bottom + pad, vh - margin);
    const visLeft   = Math.max(rect.left   - pad, margin);
    const visRight  = Math.min(rect.right  + pad, vw - margin);
    setSpotRect(new DOMRect(visLeft, visTop, visRight - visLeft, visBottom - visTop));

    const clampTop  = (t: number) => Math.min(Math.max(t, margin), vh - CARD_HEIGHT - margin);
    const clampLeft = (l: number) => Math.min(Math.max(l, margin), vw - CARD_WIDTH  - margin);

    const gap = 14;
    const spaceRight  = vw - rect.right  - gap;
    const spaceLeft   = rect.left        - gap;
    const spaceBottom = vh - rect.bottom - gap;

    // Elementos da navbar: preferir card abaixo (já estão no topo da tela)
    if (isNavItem && spaceBottom >= CARD_HEIGHT + margin) {
      setCardPos({ top: rect.bottom + gap, left: clampLeft(rect.left + rect.width / 2 - CARD_WIDTH / 2), placement: 'bottom' });
    } else if (spaceRight >= CARD_WIDTH + margin) {
      setCardPos({ top: clampTop(rect.top), left: rect.right + gap, placement: 'right' });
    } else if (spaceLeft >= CARD_WIDTH + margin) {
      setCardPos({ top: clampTop(rect.top), left: rect.left - gap - CARD_WIDTH, placement: 'left' });
    } else if (spaceBottom >= CARD_HEIGHT + margin) {
      setCardPos({ top: rect.bottom + gap, left: clampLeft(rect.left + rect.width / 2 - CARD_WIDTH / 2), placement: 'bottom' });
    } else {
      setCardPos({ top: clampTop(vh / 2 - CARD_HEIGHT / 2), left: clampLeft(vw / 2 - CARD_WIDTH / 2), placement: 'bottom' });
    }
    setReady(true);
  }, [current.id]);

  useEffect(() => {
    setReady(false);
    /*
     * Nada de rolar com a introdução na frente (22/10). Rolava, e o "Comece por
     * aqui" fechando logo antes devolvia a página ao topo por baixo dela; ao
     * clicar em "Ver tour completo" o passo 1 não rolava de novo, e o card
     * ficava espremido no pé da tela, cortado.
     */
    if (atIntro || minimizado) return;
    const el = document.getElementById(current.id);
    /**
     * Nem todo passo tem elemento sempre: a faixa de saldo some se a rota de
     * análise falhar, e o campo único não existe enquanto ela edita um
     * lançamento. Antes isso travava o tour — sem card e sem holofote, o
     * único caminho era fechar. Pular na direção em que ela estava indo
     * mantém o "Próximo" e o "←" honestos; se não houver para onde pular,
     * o tour acabou.
     */
    if (!el) {
      const alvo = step + rumo;
      if (alvo >= 0 && alvo < STEPS.length) setStep(alvo);
      else fecharRef.current();
      return;
    }

    let expandTimer: ReturnType<typeof setTimeout>;
    let scrollTimer: ReturnType<typeof setTimeout>;
    let rafId: number;
    let cancelled = false;

    const startScrollTracking = () => {
      if (cancelled) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Elementos da navbar já estão visíveis — só calcular posição
      if (naNavbar(el) && rect.top >= 0 && rect.bottom <= vh) {
        updatePosition();
        return;
      }

      const isTall = rect.height > vh * 0.6;
      /*
       * Sem espaço para o card ao lado, ele vai embaixo, e centralizar o alvo
       * deixa só meia tela para ele (22/10: numa tela baixa não cabia nem acima
       * nem abaixo, e cobria o campo). Então o alvo sobe para perto do topo.
       */
      const vw = window.innerWidth;
      const cabeAoLado = vw - rect.right >= CARD_WIDTH + 30 || rect.left >= CARD_WIDTH + 30;
      if (!isTall && !cabeAoLado) {
        window.scrollBy({ top: rect.top - 80, behavior: 'smooth' });
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: isTall ? 'start' : 'center' });
      }

      let lastY = window.scrollY;
      let stableFrames = 0;

      const checkStop = () => {
        if (cancelled) return;
        const y = window.scrollY;
        if (y === lastY) {
          stableFrames++;
          if (stableFrames >= 4) { updatePosition(); return; }
        } else {
          stableFrames = 0;
          lastY = y;
        }
        rafId = requestAnimationFrame(checkStop);
      };

      scrollTimer = setTimeout(() => {
        if (cancelled) return;
        lastY = window.scrollY;
        rafId = requestAnimationFrame(checkStop);
      }, 60);
    };

    /*
     * Abrir a seção colapsada saiu daqui em 27/08.
     *
     * A faixa de dicas precisa da MESMA regra — apontar para dentro de uma
     * dobra fechada ilumina o nada — e duas cópias divergiriam no dia em que
     * a classe do botão mudasse. Só a espera é local: o tour precisa dela
     * porque mede posição, e a faixa só rola.
     */
    if (expandirSeColapsada(el)) {
      expandTimer = setTimeout(startScrollTracking, 300);
    } else {
      startScrollTracking();
    }

    return () => {
      cancelled = true;
      clearTimeout(expandTimer);
      clearTimeout(scrollTimer);
      cancelAnimationFrame(rafId);
    };
  }, [step, rumo, current.id, updatePosition, atIntro, minimizado, retomada]);

  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [updatePosition]);

  /**
   * `CARD_HEIGHT` é um chute, e só pode ser: a escolha do lado acontece antes
   * de existir card para medir. Mas o card real é bem mais alto que 260 (com
   * quatro dicas passa de 330), e um `top` calculado sobre o chute empurra o
   * rodapé para fora da janela.
   *
   * Onde isso virou beco sem saída: a bolha do chat mora no canto INFERIOR
   * direito. O card vai para a esquerda dela, colado na base — e o "Próximo"
   * caía abaixo do fim da tela. Sem botão visível, o tour não continuava.
   *
   * Medir depois de montado é o único jeito honesto: a altura das dicas
   * depende de quantas linhas cada uma quebra, que depende da largura.
   */
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const margin = 8;
    const limite = Math.max(margin, window.innerHeight - el.offsetHeight - margin);
    /*
     * Embaixo do alvo e sem caber: subir até o limite punha o card POR CIMA do
     * que ele explica (22/10, o campo de "Lançar escrevendo" numa tela baixa).
     * Se couber acima do alvo, vai para cima.
     */
    const alvo = document.getElementById(current.id)?.getBoundingClientRect();
    const gap = 14;
    setCardPos(p => {
      if (p.top <= limite) return p;
      if (p.placement === 'bottom' && alvo && alvo.top - gap - el.offsetHeight >= margin) {
        return { ...p, top: alvo.top - gap - el.offsetHeight, placement: 'top' };
      }
      return { ...p, top: limite };
    });
  }, [ready, step, cardPos.top, current.id]);

  const go = (next: number) => {
    setReady(false);
    setRumo(next >= step ? 1 : -1);
    setStep(next);
  };

  if (atIntro) {
    return (
      <div className="tm-intro-overlay" onClick={onClose}>
        <div className="tm-intro-card" onClick={e => e.stopPropagation()}>
          <div className="tm-intro-badge">novidades</div>
          <h2 className="tm-intro-title">Tudo que você pode fazer no FinTracking</h2>
          <p className="tm-intro-desc">
            Vamos te mostrar todas as ferramentas disponíveis para você organizar suas finanças com mais facilidade.
          </p>
          <ul className="tm-intro-highlights">
            <li><span>⚡</span> Lançar escrevendo: <em>gastei 50 em uber no crédito</em></li>
            <li><span>💬</span> Perguntar sobre o seu dinheiro, e simular antes de comprar</li>
            {/* 22/10: Dívidas e Modo escuro saíram para a projeção e a newsletter. */}
            <li><span>📊</span> Quanto vai sobrar em cada um dos próximos meses</li>
            <li><span>📱</span> Lançar e consultar pelo WhatsApp, sem abrir o site</li>
            <li><span>📰</span> Newsletter diária: seus números e as notícias, nos dias úteis</li>
          </ul>
          <div className="tm-intro-actions">
            <button className="tm-intro-btn-skip" onClick={onClose}>Agora não</button>
            <button className="tm-intro-btn-start" onClick={() => setAtIntro(false)}>
              Ver tour completo →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (minimizado) {
    return (
      <div className="tm-retomar" role="region" aria-label="Tour minimizado">
        <button
          className="tm-retomar-btn"
          style={{ background: acento, color: sobreAcento }}
          onClick={() => { setMinimizado(false); setRetomada(n => n + 1); }}
        >
          Continuar tour · {step + 1}/{STEPS.length}
        </button>
        <button className="tm-retomar-fechar" onClick={onClose} aria-label="Fechar o tour">✕</button>
      </div>
    );
  }

  return (
    <>
      {/* Overlay escuro: clicar fora minimiza, para ela experimentar e voltar. */}
      <div className="tm-overlay" onClick={() => setMinimizado(true)} title="Minimizar o tour" />

      {/* Spotlight na seção ativa */}
      {spotRect && ready && (
        <>
          {/* Anel de pulso */}
          <div
            className="tm-spotlight-pulse"
            style={{
              top:    spotRect.top    - 10,
              left:   spotRect.left   - 10,
              width:  spotRect.width  + 20,
              height: spotRect.height + 20,
              borderColor: acento,
            }}
          />
          {/* Spotlight principal */}
          <div
            className="tm-spotlight"
            style={{
              top:    spotRect.top    - 5,
              left:   spotRect.left   - 5,
              width:  spotRect.width  + 10,
              height: spotRect.height + 10,
              borderColor: acento,
              boxShadow: [
                `0 0 0 9999px rgba(0,0,0,0.58)`,
                `0 0 0 4px ${acento}35`,
                `0 0 24px 4px ${acento}55`,
              ].join(', '),
            }}
          />
        </>
      )}

      {/* Card de instruções */}
      {ready && (
        <div
          ref={cardRef}
          className={`tm-card tm-card-${cardPos.placement}${sheetNoTopo ? ' tm-sheet-topo' : ''}`}
          style={{ top: cardPos.top, left: cardPos.left, width: CARD_WIDTH }}
          onClick={e => e.stopPropagation()}
        >
          {/* Seta apontando para a seção */}
          <div className="tm-arrow" style={{ background: acento }} />

          <div className="tm-card-header" style={{ background: acento + '14', borderColor: acento + '30' }}>
            <span className="tm-card-icon">{current.icon}</span>
            <div>
              <h3 className="tm-card-title" style={{ color: acento }}>{current.title}</h3>
              <p className="tm-card-desc">{current.desc}</p>
            </div>
          </div>

          <ul className="tm-tips">
            {current.tips.map((tip, i) => (
              <li key={i} className="tm-tip">
                <span className="tm-tip-dot" style={{ background: acento }} />
                {tip}
              </li>
            ))}
          </ul>

          <div className="tm-card-footer">
            <span className="tm-counter" style={{ color: acento }}>
              {step + 1} / {STEPS.length}
            </span>
            <div className="tm-actions">
              {step > 0 && (
                <button className="tm-btn-prev" onClick={() => go(step - 1)}>←</button>
              )}
              <button className="tm-btn-skip" onClick={onClose}>Pular</button>
              <button
                className="tm-btn-next"
                style={{ background: acento, color: sobreAcento }}
                onClick={() => isLast ? onClose() : go(step + 1)}
              >
                {isLast ? 'Concluir ✓' : 'Próximo →'}
              </button>
            </div>
          </div>

          {/* Dots */}
          <div className="tm-dots">
            {STEPS.map((_, i) => (
              <button
                key={i}
                className={`tm-dot${i === step ? ' tm-dot-active' : ''}`}
                style={i === step ? { background: acento } : {}}
                onClick={() => go(i)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default TutorialModal;
